import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';

import { getAndroidPlayStoreUrl, getAndroidUpdateMode } from '../config/runtime';

const ANDROID_PACKAGE_NAME = 'ai.okio.app';
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
const LAST_CHECK_STORAGE_KEY = 'okio:android-app-update:last-check-at';
const FLEXIBLE_DOWNLOAD_TOAST_ID = 'okio:android-app-update:downloading';
const FLEXIBLE_RESTART_TOAST_ID = 'okio:android-app-update:restart';

let pluginModulePromise = null;
let hasInitialized = false;
let activeCheckPromise = null;
let activeFlexibleListener = null;

const isAndroidNative = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true && Capacitor.getPlatform() === 'android';
  } catch (_) {
    return false;
  }
};

const getStoredLastCheckAt = () => {
  try {
    const rawValue = window.localStorage.getItem(LAST_CHECK_STORAGE_KEY);
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  } catch (_) {
    return 0;
  }
};

const setStoredLastCheckAt = (value) => {
  try {
    window.localStorage.setItem(LAST_CHECK_STORAGE_KEY, String(value));
  } catch (_) {}
};

const shouldCheckNow = ({ force = false } = {}) => {
  if (force) return true;
  return Date.now() - getStoredLastCheckAt() >= CHECK_INTERVAL_MS;
};

const loadPlugin = async () => {
  if (!pluginModulePromise) {
    pluginModulePromise = import('@capawesome/capacitor-app-update');
  }
  return pluginModulePromise;
};

const openPlayStoreFallback = async (AppUpdate) => {
  try {
    await AppUpdate.openAppStore({ androidPackageName: ANDROID_PACKAGE_NAME });
    return true;
  } catch (error) {
    console.warn('[AppUpdate] Failed to open Play Store via plugin:', error);
  }

  const playStoreUrl = getAndroidPlayStoreUrl(ANDROID_PACKAGE_NAME);
  try {
    if (typeof window !== 'undefined' && typeof window.open === 'function') {
      window.open(playStoreUrl, '_blank', 'noopener,noreferrer');
      return true;
    }
  } catch (error) {
    console.warn('[AppUpdate] Failed to open Play Store via window.open:', error);
  }

  try {
    if (typeof window !== 'undefined' && window.location) {
      window.location.assign(playStoreUrl);
      return true;
    }
  } catch (error) {
    console.warn('[AppUpdate] Failed to redirect to Play Store:', error);
  }

  return false;
};

const showRestartToast = (completeFlexibleUpdate) => {
  toast.dismiss(FLEXIBLE_DOWNLOAD_TOAST_ID);

  toast.custom(
    (toastInstance) => (
      <div className="w-[min(90vw,28rem)] rounded-2xl border border-emerald-500/30 bg-slate-900 p-4 text-white shadow-2xl">
        <div className="text-sm font-semibold">Update ready to install</div>
        <div className="mt-1 text-sm text-slate-300">
          Restart Okio to finish installing the latest version.
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-medium text-white"
            onClick={async () => {
              toast.dismiss(toastInstance.id);
              try {
                await completeFlexibleUpdate();
              } catch (error) {
                console.warn('[AppUpdate] Failed to complete flexible update:', error);
                toast.error('Could not restart to finish the update.');
              }
            }}
          >
            Restart now
          </button>
          <button
            className="rounded-lg border border-slate-600 px-3 py-2 text-sm font-medium text-slate-200"
            onClick={() => toast.dismiss(toastInstance.id)}
          >
            Later
          </button>
        </div>
      </div>
    ),
    {
      id: FLEXIBLE_RESTART_TOAST_ID,
      duration: Infinity,
    }
  );
};

const promptForStoreFallback = async (AppUpdate) => {
  const shouldOpenStore = typeof window === 'undefined'
    ? false
    : window.confirm(
        'A newer Okio version is available. Open the Play Store to update now?'
      );

  if (!shouldOpenStore) {
    return false;
  }

  const opened = await openPlayStoreFallback(AppUpdate);
  if (!opened) {
    toast.error('Could not open the Play Store.');
  }
  return opened;
};

const handleFlexibleUpdateStateChange = async (state, AppUpdate, FlexibleUpdateInstallStatus) => {
  switch (state?.installStatus) {
    case FlexibleUpdateInstallStatus.DOWNLOADING:
      toast.loading('Downloading the latest Okio update...', {
        id: FLEXIBLE_DOWNLOAD_TOAST_ID,
      });
      break;
    case FlexibleUpdateInstallStatus.DOWNLOADED:
      showRestartToast(() => AppUpdate.completeFlexibleUpdate());
      break;
    case FlexibleUpdateInstallStatus.INSTALLED:
      toast.dismiss(FLEXIBLE_DOWNLOAD_TOAST_ID);
      toast.dismiss(FLEXIBLE_RESTART_TOAST_ID);
      break;
    case FlexibleUpdateInstallStatus.CANCELED:
      toast.dismiss(FLEXIBLE_DOWNLOAD_TOAST_ID);
      toast('Update download canceled.', { id: FLEXIBLE_DOWNLOAD_TOAST_ID });
      break;
    case FlexibleUpdateInstallStatus.FAILED:
      toast.dismiss(FLEXIBLE_DOWNLOAD_TOAST_ID);
      toast.error('Update download failed. You can try again later.');
      break;
    default:
      break;
  }
};

const initializeFlexibleUpdateListener = async () => {
  if (!isAndroidNative() || activeFlexibleListener) return;

  const { AppUpdate, FlexibleUpdateInstallStatus } = await loadPlugin();
  activeFlexibleListener = await AppUpdate.addListener(
    'onFlexibleUpdateStateChange',
    (state) => {
      handleFlexibleUpdateStateChange(state, AppUpdate, FlexibleUpdateInstallStatus).catch(
        (error) => {
          console.warn('[AppUpdate] Flexible update listener failed:', error);
        }
      );
    }
  );
};

const performUpdateFlow = async (info, plugin) => {
  const {
    AppUpdate,
    AppUpdateAvailability,
    AppUpdateResultCode,
    FlexibleUpdateInstallStatus,
  } = plugin;

  if (info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
    showRestartToast(() => AppUpdate.completeFlexibleUpdate());
    return { status: 'downloaded' };
  }

  if (
    info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE &&
    info.updateAvailability !== AppUpdateAvailability.UPDATE_IN_PROGRESS
  ) {
    return { status: 'up-to-date' };
  }

  if (info.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS) {
    return { status: 'in-progress' };
  }

  const preferredMode = getAndroidUpdateMode();

  if (preferredMode === 'immediate' && info.immediateUpdateAllowed) {
    const result = await AppUpdate.performImmediateUpdate();
    return { status: 'immediate', result };
  }

  if (info.flexibleUpdateAllowed) {
    const result = await AppUpdate.startFlexibleUpdate();
    if (result?.code === AppUpdateResultCode.OK) {
      toast.success('Update accepted. Okio will download it in the background.');
      return { status: 'flexible-started', result };
    }
    if (result?.code === AppUpdateResultCode.CANCELED) {
      toast('Update skipped for now.');
      return { status: 'cancelled', result };
    }
    if (result?.code === AppUpdateResultCode.NOT_ALLOWED && info.immediateUpdateAllowed) {
      const immediateResult = await AppUpdate.performImmediateUpdate();
      return { status: 'immediate-fallback', result: immediateResult };
    }
  }

  if (info.immediateUpdateAllowed) {
    const result = await AppUpdate.performImmediateUpdate();
    return { status: 'immediate-fallback', result };
  }

  await promptForStoreFallback(AppUpdate);
  return { status: 'store-fallback' };
};

const runCheck = async ({ force = false, source = 'manual' } = {}) => {
  if (!isAndroidNative()) {
    return { status: 'unsupported' };
  }

  if (!shouldCheckNow({ force })) {
    return { status: 'throttled' };
  }

  await initializeFlexibleUpdateListener();

  const plugin = await loadPlugin();
  const info = await plugin.AppUpdate.getAppUpdateInfo();

  setStoredLastCheckAt(Date.now());

  const result = await performUpdateFlow(info, plugin);
  console.log('[AppUpdate] check completed:', {
    source,
    status: result?.status,
    availability: info?.updateAvailability,
    availableVersionCode: info?.availableVersionCode,
  });
  return result;
};

export const AppUpdateService = {
  async init() {
    if (!isAndroidNative() || hasInitialized) {
      return { enabled: false };
    }

    try {
      await initializeFlexibleUpdateListener();
      hasInitialized = true;
      return { enabled: true };
    } catch (error) {
      console.warn('[AppUpdate] init failed:', error);
      return { enabled: false, error: error?.message };
    }
  },

  async checkForUpdates(options = {}) {
    if (!isAndroidNative()) {
      return { status: 'unsupported' };
    }

    if (activeCheckPromise) {
      return activeCheckPromise;
    }

    activeCheckPromise = runCheck(options).catch(async (error) => {
      console.warn('[AppUpdate] check failed:', error);
      try {
        const { AppUpdate } = await loadPlugin();
        if (options?.forceStoreFallback) {
          await openPlayStoreFallback(AppUpdate);
        }
      } catch (_) {}
      return { status: 'error', error: error?.message };
    });

    try {
      return await activeCheckPromise;
    } finally {
      activeCheckPromise = null;
    }
  },

  async destroy() {
    try {
      await activeFlexibleListener?.remove?.();
    } catch (_) {}
    activeFlexibleListener = null;
    hasInitialized = false;
  },
};

export default AppUpdateService;
